import React, { useCallback, useEffect, useRef } from 'react'
import Link from 'next/link'

import {
  Loader,
  Segment,
  Container,
  GridColumn,
  Grid,
  GridRow,
  ListItem,
  List,
  Button,
  Divider,
  ListHeader
} from 'semantic-ui-react'
import _ from 'lodash'

import useMediaQuery from 'hooks/useMediaQuery'
import { handleDirectionByAddress as directionByAddress } from 'helpers/linkingActions'
import { format } from 'date-fns'
import { convertToTargetTimeZone } from '@web/helpers/getTimeZone'
import { getDateLocale, activeLanguage } from 'providers/Localization/i18n'
import DOMPurify from 'dompurify'

import themeStyles from './styles'
import cssStyles from './styles.module.css'
import { useTheme } from 'contexts/ThemeProvider'

import { localization } from 'containers/Appointments/localization'
import {
  IconPhone,
  IconCompose,
  IconTeleAppts,
  IconErrorMessages
} from '@images/svg'
import withLocalization from '@web/helpers/localizationUtils'
import { useSelector } from 'react-redux'
import { getExternalLinks, getZoomLinks } from '@web/stores/Links/Select'
import JoinInButton from '@web/containers/Appointments/components/UpcomingCard/JoinInButton'
import { useAppointmentIcs } from 'src/hooks/appointments/useAppointmentIcs'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { openMobileApp } from 'helpers/openMobileApp'
import { MdCalendarMonth } from 'react-icons/md'
import { FaRegCheckCircle } from 'react-icons/fa'
import { ErrorModal, InfoModal } from 'src/components/ModalTemplates'
import { useModal } from 'src/hooks/useModal'

export const findMatchingProvider = (zoomLinks, providerName) => {
  if (
    !_.isArray(zoomLinks) ||
    _.isEmpty(zoomLinks) ||
    !_.isString(providerName)
  ) {
    return null
  }
  return (
    _.find(zoomLinks, ({ FirstName, LastName }) => {
      if (!_.isString(FirstName) || !_.isString(LastName)) {
        return false
      }

      const parts = _.map(providerName.split(','), _.trim)
      if (parts.length < 2) {
        return false
      }
      const [part1, part2] = parts

      return (
        (part1 === FirstName && _.includes(part2, LastName)) ||
        (part1 === LastName && _.includes(part2, FirstName))
      )
    }) || null
  )
}
export const isSameDay = (d1, d2) => d1?.toDateString() === d2?.toDateString()

function AppointmentCard(props) {
  const { theme } = useTheme()
  const styles = themeStyles(theme)
  const t = _.get(props, 't')
  const {
    download: downloadAppointmentAsIcs,
    isLoading: isLoadingIcsFile,
    error: icsError,
    result: icsResult,
    reset: resetIcs
  } = useAppointmentIcs()
  const { show: showErrorModalContent } = useModal(
    `${props?.appointment?.appointmentId}-download-ics-error-modal`
  )
  const icsErrorRef = useRef(null)
  const { show: showSuccessModalContent, hide: hideSuccessModal } = useModal(
    `${props?.appointment?.appointmentId}-download-ics-success-modal`
  )
  const icsSuccessRef = useRef(null)
  const icsErrorMessages = {
    INVALID_DATA: {
      title: t('icsErrorInvalidTitle'),
      message: t('icsErrorInvalidSubtitle')
    },
    DOWNLOAD_FAILED: {
      title: t('icsErrorDownloadTitle'),
      message: t('icsErrorDownloadSubtitle')
    }
  }
  const icsErrorContent = icsError?.code
    ? icsErrorMessages[icsError.code] || {
        title: t('error'),
        message: t('errorSubtitle')
      }
    : {
        title: t('error'),
        message: t('errorSubtitle')
      }
  const showErrorModal = useCallback(() => {
    showErrorModalContent({
      preventClose: true,
      render: ({ hideModal }) => (
        <ErrorModal
          hideModal={hideModal}
          title={icsErrorContent.title}
          message={icsErrorContent.message}
          renderIcon={() => (
            <IconErrorMessages color={'#BDBDBD'} width={60} height={60} />
          )}
          actions={[
            {
              label: 'close',
              onPress: hideModal
            }
          ]}
        />
      )
    })
  }, [showErrorModalContent, icsErrorContent.title, icsErrorContent.message])
  useEffect(() => {
    if (icsError && icsError !== icsErrorRef.current) {
      icsErrorRef.current = icsError
      showErrorModal()
    }
    if (!icsError) {
      icsErrorRef.current = null
    }
  }, [icsError, showErrorModal])
  const showSuccessModal = useCallback(() => {
    showSuccessModalContent({
      render: () => (
        <InfoModal
          showDismissLabel={false}
          message={t('icsSuccessSubtitle')}
          renderIcon={() => <FaRegCheckCircle style={styles.successIcon} />}
        />
      )
    })
    setTimeout(() => {
      hideSuccessModal()
      resetIcs()
    }, 5000)
  }, [
    showSuccessModalContent,
    hideSuccessModal,
    resetIcs,
    styles.successIcon,
    t
  ])
  useEffect(() => {
    if (
      icsResult?.action === 'downloaded' &&
      icsResult !== icsSuccessRef.current
    ) {
      icsSuccessRef.current = icsResult
      showSuccessModal()
    }
    if (!icsResult) {
      icsSuccessRef.current = null
    }
  }, [icsResult, showSuccessModal])
  const workflowUrl = _.get(props.workflow, 'workflowUrl')
  const workflowStatus = _.get(props.workflow, 'workflowStatus')
  const isStartedCheckin = workflowStatus === 'Started'
  const isCompletedCheckin = workflowStatus === 'Completed'
  const isNotStartedCheckin = workflowStatus === 'NotStarted'
  const patientInstructions = DOMPurify.sanitize(
    _.get(props, 'patientInstructions', '')
  )
  const formattedPatientInstructions = patientInstructions.replace(
    /\r\n/g,
    '<br />'
  )
  const isTelehealth = _.includes(
    _.toLower(props?.appointmentType || ''),
    'telehealth'
  )
  let checkinMark
  if (isNotStartedCheckin) {
    checkinMark = 'checkin'
  } else if (isStartedCheckin) {
    checkinMark = 'checkin'
  } else if (isCompletedCheckin) {
    checkinMark = 'completedCheckin'
  }
  const address = _.join(
    [
      _.get(props.departmentAddress, 'street'),
      _.get(props.departmentAddress, 'city'),
      _.get(props.departmentAddress, 'state'),
      _.get(props.departmentAddress, 'zip'),
      _.get(props.departmentAddress, 'country')
    ],
    ' '
  ).trim()
  const streetLines = _.get(props.departmentAddress, 'streetLines') || []
  const currCheckinAppointmentId = _.get(props, 'currCheckinAppointmentId', '')
  const setCurrCheckinAppointmentId = _.get(
    props,
    'setCurrCheckinAppointmentId'
  )
  const loadingCheckin = _.get(props, 'loadingCheckin')
  const phoneNumber = _.get(props, 'departmentPhones[0].number', '')
  const selectedAppointmentId = _.get(props, 'selectedAppointmentId', '')
  const setSelectedAppointmentId = _.get(props, 'setSelectedAppointmentId', '')
  const appointmentId = _.get(props, 'appointmentId')
  const { isLargeMonitor, isMobile } = useMediaQuery()
  const { isMobileDevice } = useIsMobileDevice()
  const expandFlag = () => {
    return selectedAppointmentId === appointmentId
  }
  const datetimeObject = new Date(`${props.date} ${props.time}`)
  const result = convertToTargetTimeZone(datetimeObject, props.timeZone)
  const month = result.error
    ? '-'
    : format(result.targetDate, 'MMMM', {
        locale: getDateLocale(activeLanguage())
      })
  const day = result.error ? '-' : format(result.targetDate, 'dd')
  const year = result.error ? '-' : format(result.targetDate, 'yyyy')
  const time = result.error
    ? result.error
    : `${format(result.targetDate, 'h:mm aaa')} ${result.timeZoneAbbreviation}`
  const zoomLinks = useSelector(getZoomLinks)
  const externalLinks = useSelector(getExternalLinks)
  const otherContent = _.get(externalLinks, 'OtherContent', {})
  const timeInSF = _.get(otherContent, 'UFD_A_Temporarily_Used_Time', '')
  const appointmentDateTime = result.error ? '-' : result.targetDate
  const testUsingTime = timeInSF ? new Date(timeInSF) : ''
  const todayObject = new Date()

  const changSelectedAppointmentIdHandler = () => {
    if (expandFlag()) {
      setSelectedAppointmentId('')
      return
    }
    setSelectedAppointmentId(appointmentId)
  }
  const handleDirectionByAddress = address => {
    if (!_.isEmpty(address)) {
      directionByAddress(address)
    }
  }

  const CheckinMark = () => {
    return <span style={styles[checkinMark]}>{t(checkinMark)}</span>
  }

  const CheckInButton = () => {
    return (
      <a
        onClick={() =>
          setCurrCheckinAppointmentId &&
          setCurrCheckinAppointmentId(appointmentId)
        }
        href={workflowUrl}
        rel={'noreferrer'}
        target={'_blank'}
        style={isMobile ? styles.checkinButtonMobile : styles.checkinButton}
        tabIndex={-1}
      >
        <button style={styles.buttonCheckin}>{t('checkinText')}</button>
      </a>
    )
  }

  const matchingProvider = findMatchingProvider(
    zoomLinks,
    _.get(props, 'providerName', '')
  )

  const handleAddToCalendarClick = () => {
    if (isLoadingIcsFile) {
      return
    }
    if (isMobileDevice) {
      openMobileApp({
        route: 'Appointments',
        params: { appointmentId },
        onFallback: () => downloadAppointmentAsIcs(props?.appointment)
      })
      return
    }
    downloadAppointmentAsIcs(props?.appointment)
  }

  const ActionArea = () => {
    return (
      <div style={styles.buttonGroup}>
        {phoneNumber ? (
          <>
            <a
              href={`tel:${phoneNumber}`}
              style={styles.fisFooterButtons}
              tabIndex={-1}
            >
              <button style={styles.buttons}>
                <IconPhone style={styles.phoneIcon} />
                <span>{t('call')}</span>
              </button>
            </a>
          </>
        ) : (
          <>
            <div style={styles.footerButtons} tabIndex={-1}>
              <button disabled style={styles.buttonsDisabled}>
                <IconPhone style={styles.phoneIconDisabled} />
                <span>{t('call')}</span>
              </button>
            </div>
          </>
        )}
        <div style={styles.divider} />
        <Link href={'/messages'} style={styles.footerButtons} tabIndex={-1}>
          <button style={styles.buttonSend}>
            <IconCompose style={styles.phoneIcon} />
            <span>{t('sendMessage')}</span>
          </button>
        </Link>
        <div style={styles.divider} />
        <button
          onClick={handleAddToCalendarClick}
          disabled={isLoadingIcsFile}
          style={styles.buttonCalendar}
        >
          {isLoadingIcsFile ? (
            <Loader active inline size='small' />
          ) : (
            <>
              <MdCalendarMonth style={styles.calendarIcon} />
              <span>{t('addToCalendar')}</span>
            </>
          )}
        </button>
      </div>
    )
  }
  return (
    <>
      {isLargeMonitor && (
        <Segment style={styles.borderNone}>
          {loadingCheckin && currCheckinAppointmentId === appointmentId ? (
            <div style={styles.checkinText}>
              <Loader active inline />
            </div>
          ) : (
            checkinMark && (
              <div style={styles.checkinText}>
                <CheckinMark />
              </div>
            )
          )}
          {isTelehealth &&
            matchingProvider &&
            isSameDay(appointmentDateTime, testUsingTime || todayObject) && (
              <div style={styles.joinInText}>
                <JoinInButton
                  zoomLink={_.get(matchingProvider, 'ZoomLink', '')}
                />
              </div>
            )}
          <Container>
            <Grid style={styles.appointmentCard()} columns={16}>
              <GridRow>
                <GridColumn width={2}>
                  <div style={styles.calendarCard}>
                    <div style={styles.month}>{month.toUpperCase()}</div>
                    <div style={styles.day}>{day}</div>
                    <div style={styles.year}>{year}</div>
                  </div>
                </GridColumn>
                <GridColumn width={13}>
                  <List divided horizontal style={styles.listTitle}>
                    <ListItem>
                      <span style={styles.listTitle}>
                        {_.get(props, 'appointmentType')}
                      </span>
                    </ListItem>
                    <ListItem>
                      <span style={styles.listTitle}>{time}</span>
                    </ListItem>
                  </List>
                  <List style={styles.listAddToCalendarTitle}>
                    <ListItem onClick={handleAddToCalendarClick}>
                      {isLoadingIcsFile ? (
                        <Loader active inline size='small' />
                      ) : (
                        <span
                          style={styles.addToCalendarTitleLabel}
                          className={cssStyles.addToCalendarLabel}
                        >
                          [ {t('addToCalendar')} ]
                        </span>
                      )}
                    </ListItem>
                  </List>
                  <List style={styles.listSubTitle}>
                    <ListItem>
                      {!_.isEmpty(_.get(props, 'providerName'))
                        ? t('yourVisitWith')
                        : ''}
                    </ListItem>
                  </List>
                  <List style={styles.listThirdTitle}>
                    <ListItem>{_.get(props, 'providerName')}</ListItem>
                  </List>
                </GridColumn>
                <GridColumn width={1} style={styles.button}>
                  <Button
                    tabIndex={0}
                    style={styles.expandButton}
                    onClick={() => changSelectedAppointmentIdHandler()}
                    icon={expandFlag() ? 'angle up' : 'angle down'}
                  />
                </GridColumn>
              </GridRow>
            </Grid>
          </Container>
          {expandFlag() && (
            <>
              <Divider style={styles.customDivider} />
              <Container>
                <List>
                  {workflowUrl && !isCompletedCheckin && (
                    <ListItem>
                      <CheckInButton />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListHeader style={styles.listHeader}>
                      {t('appointmentLocation')}
                    </ListHeader>
                  </ListItem>
                  <ListItem />
                  <ListItem />
                  {isTelehealth ? (
                    <div style={styles.teleHealth}>
                      <IconTeleAppts style={styles.teleHealthIcon} />
                      <div style={styles.teleHealthText}>
                        {t('telehealthText')}
                      </div>
                    </div>
                  ) : (
                    <>
                      {streetLines.map((street, index) => (
                        <ListItem key={index}>{street}</ListItem>
                      ))}
                      <ListItem>
                        {_.get(props.departmentAddress, 'city')}{' '}
                        {_.get(props.departmentAddress, 'state')}{' '}
                        {_.get(props.departmentAddress, 'zip')}{' '}
                        {_.get(props.departmentAddress, 'country')}
                      </ListItem>
                      <ListItem />
                      <ListItem
                        as='button'
                        onClick={() => handleDirectionByAddress(address)}
                        style={styles.getDirections(_.isEmpty(address))}
                      >
                        {t('getDirections')} <span>&gt;</span>
                      </ListItem>
                    </>
                  )}
                </List>
              </Container>
              <Divider style={styles.customDivider} />
              <Container>
                <List>
                  <ListItem>
                    <ListHeader style={styles.listHeader}>
                      {t('beforeYouComeIn')}
                    </ListHeader>
                  </ListItem>
                  <ListItem />
                  <ListItem>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formattedPatientInstructions
                      }}
                    />
                  </ListItem>
                </List>
              </Container>
              <div style={styles.placeholder} />
              <ActionArea />
            </>
          )}
        </Segment>
      )}

      {!isLargeMonitor && (
        <Segment style={styles.borderNone}>
          <Container>
            <Grid columns='equal'>
              <GridColumn width={3}>
                <List style={styles.mobileCalendarCard}>
                  <ListItem style={styles.monthMobile}>
                    {_.take(month, 3).join('').toUpperCase()}
                  </ListItem>
                  <ListItem style={styles.dayMobile}>{day}</ListItem>
                  <ListItem style={styles.yearMobile}>{year}</ListItem>
                </List>
              </GridColumn>
              <GridColumn width={13}>
                <Button
                  style={styles.expandButtonMobile}
                  onClick={() => changSelectedAppointmentIdHandler()}
                  icon={expandFlag() ? 'angle up' : 'angle down'}
                />
                <List style={styles.listTitle}>
                  <ListItem>
                    <span style={styles.listTitle}>
                      {_.get(props, 'appointmentType')}
                    </span>
                  </ListItem>
                  <ListItem>
                    <span style={styles.listTitle}>{time}</span>
                  </ListItem>
                  {loadingCheckin &&
                  currCheckinAppointmentId === appointmentId ? (
                    <ListItem>
                      <Loader active inline />
                    </ListItem>
                  ) : (
                    checkinMark && (
                      <ListItem>
                        <CheckinMark />
                      </ListItem>
                    )
                  )}
                  {isTelehealth &&
                    matchingProvider &&
                    isSameDay(
                      appointmentDateTime,
                      testUsingTime || todayObject
                    ) && (
                      <ListItem>
                        <JoinInButton
                          zoomLink={_.get(matchingProvider, 'ZoomLink', '')}
                        />
                      </ListItem>
                    )}
                </List>
                <List>
                  <ListItem onClick={handleAddToCalendarClick}>
                    {isLoadingIcsFile ? (
                      <Loader active inline size='small' />
                    ) : (
                      <span>[ {t('addToCalendar')} ]</span>
                    )}
                  </ListItem>
                  <ListItem style={styles.listSubTitleMobile}>
                    {!_.isEmpty(_.get(props, 'providerName'))
                      ? t('yourVisitWith')
                      : ''}
                  </ListItem>
                  <ListItem />
                  <ListItem style={styles.listThirdTitleMobile}>
                    {_.get(props, 'providerName')}
                  </ListItem>
                </List>
              </GridColumn>
            </Grid>
          </Container>
          {expandFlag() && (
            <>
              <Divider style={styles.customDivider} />
              <Container>
                <List>
                  {workflowUrl && !isCompletedCheckin && (
                    <ListItem>
                      <CheckInButton />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListHeader style={styles.listHeader}>
                      {t('appointmentLocation')}
                    </ListHeader>
                  </ListItem>
                  <ListItem />
                  {isTelehealth ? (
                    <div style={styles.teleHealth}>
                      <IconTeleAppts style={styles.teleHealthIcon} />
                      <div style={styles.teleHealthText}>
                        {t('telehealthText')}
                      </div>
                    </div>
                  ) : (
                    <>
                      {streetLines.map((street, index) => (
                        <ListItem key={index}>{street}</ListItem>
                      ))}
                      <ListItem>
                        {_.get(props.departmentAddress, 'city')}{' '}
                        {_.get(props.departmentAddress, 'state')}{' '}
                        {_.get(props.departmentAddress, 'zip')}{' '}
                        {_.get(props.departmentAddress, 'country')}
                      </ListItem>
                      <ListItem />
                      <ListItem
                        as='button'
                        onClick={() => handleDirectionByAddress(address)}
                        style={styles.getDirections(_.isEmpty(address))}
                      >
                        {t('getDirections')} <span>&gt;</span>
                      </ListItem>
                    </>
                  )}
                </List>
              </Container>
            </>
          )}
          {expandFlag() && (
            <>
              <Divider style={styles.customDivider} />
              <Container>
                <List>
                  <ListItem>
                    <ListHeader style={styles.listHeader}>
                      {t('beforeYouComeIn')}
                    </ListHeader>
                  </ListItem>
                  <ListItem />
                  <ListItem>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formattedPatientInstructions
                      }}
                    />
                  </ListItem>
                </List>
              </Container>
              <div style={styles.placeholder} />
              {expandFlag() && <ActionArea />}
            </>
          )}
        </Segment>
      )}
    </>
  )
}

export default withLocalization(AppointmentCard, localization)
